(() => {
  if (customElements.get('ecomhard-frete')) return;

  const delay = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  class EcomhardFrete extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.input = this.querySelector('.ecomhard-frete__input');
      this.button = this.querySelector('[data-action="calculate"]');
      this.buttonLabel = this.querySelector('.ecomhard-frete__button-label');
      this.feedback = this.querySelector('[data-feedback]');
      this.defaultButtonLabel = this.buttonLabel?.textContent?.trim() || 'CALCULAR';
      this.root = window.Shopify?.routes?.root || '/';
      this.previewKey = null;
      this.isCalculating = false;

      this.input?.addEventListener('input', () => this.formatCep());
      this.input?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.calculate();
        }
      });
      this.button?.addEventListener('click', () => this.calculate());

    }

    formatCep() {
      const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
      this.input.value = digits.length > 5
        ? `${digits.slice(0, 5)}-${digits.slice(5)}`
        : digits;
    }

    getCep() {
      return (this.input?.value || '').replace(/\D/g, '').slice(0, 8);
    }

    getProductForm() {
      const formId = this.dataset.productFormId;
      return formId ? document.getElementById(formId) : null;
    }

    getVariantId() {
      const form = this.getProductForm();
      const formId = this.dataset.productFormId;
      const externalInput = formId
        ? document.querySelector(`[name="id"][form="${this.escapeAttribute(formId)}"]`)
        : null;
      const variantInput = form?.querySelector('[name="id"]') || externalInput;
      return Number(variantInput?.value || this.dataset.variantId || 0);
    }

    getQuantity() {
      const form = this.getProductForm();
      const formId = this.dataset.productFormId;
      const externalInput = formId
        ? document.querySelector(`[name="quantity"][form="${this.escapeAttribute(formId)}"]`)
        : null;
      const quantityInput = form?.querySelector('[name="quantity"]') || externalInput;
      const quantity = Number.parseInt(quantityInput?.value || '1', 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    escapeAttribute(value) {
      if (window.CSS?.escape) return CSS.escape(value);
      return String(value).replace(/["\\]/g, '\\$&');
    }

    setLoading(isLoading) {
      this.isCalculating = isLoading;
      this.button.disabled = isLoading;
      this.setAttribute('aria-busy', String(isLoading));
      this.buttonLabel.textContent = isLoading
        ? (this.dataset.loadingLabel || 'CALCULANDO...')
        : this.defaultButtonLabel;
    }

    clearFeedback() {
      this.feedback.hidden = true;
      this.feedback.replaceChildren();
    }

    showMessage(message, isError = false) {
      const wrapper = document.createElement('div');
      wrapper.className = `ecomhard-frete__message${isError ? ' ecomhard-frete__message--error' : ''}`;
      wrapper.textContent = message;
      this.feedback.replaceChildren(wrapper);
      this.feedback.hidden = false;
    }

    showRates(rates) {
      if (!Array.isArray(rates) || rates.length === 0) {
        this.showMessage(this.dataset.noRatesLabel || 'Nenhuma opção de frete encontrada para este CEP.');
        return;
      }

      const list = document.createElement('div');
      list.className = 'ecomhard-frete__rates';

      rates.forEach((rate) => {
        const row = document.createElement('div');
        row.className = 'ecomhard-frete__rate';

        const name = document.createElement('div');
        name.className = 'ecomhard-frete__rate-name';
        name.textContent = rate.presentment_name || rate.name || 'Frete';

        const deliveryText = this.getDeliveryText(rate);
        if (deliveryText) {
          const delivery = document.createElement('span');
          delivery.className = 'ecomhard-frete__rate-delivery';
          delivery.textContent = deliveryText;
          name.appendChild(delivery);
        }

        const price = document.createElement('div');
        price.className = 'ecomhard-frete__rate-price';
        price.textContent = this.formatMoney(rate.price, rate.currency);

        row.append(name, price);
        list.appendChild(row);
      });

      this.feedback.replaceChildren(list);
      this.feedback.hidden = false;
    }

    getDeliveryText(rate) {
      if (Array.isArray(rate.delivery_days) && rate.delivery_days.length) {
        const min = Number(rate.delivery_days[0]);
        const max = Number(rate.delivery_days[rate.delivery_days.length - 1]);

        if (Number.isFinite(min) && Number.isFinite(max)) {
          return min === max
            ? `Prazo estimado: ${min} dia${min === 1 ? '' : 's'} útil${min === 1 ? '' : 'eis'}`
            : `Prazo estimado: ${min} a ${max} dias úteis`;
        }
      }

      if (rate.delivery_date) {
        const parsedDate = new Date(`${rate.delivery_date}T12:00:00`);
        if (!Number.isNaN(parsedDate.getTime())) {
          return `Entrega estimada: ${new Intl.DateTimeFormat('pt-BR').format(parsedDate)}`;
        }
      }

      return rate.description || '';
    }

    formatMoney(value, currency) {
      const numericValue = Number.parseFloat(value || 0);
      const activeCurrency = currency || this.dataset.currency || window.Shopify?.currency?.active || 'BRL';

      try {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: activeCurrency
        }).format(numericValue);
      } catch (_) {
        return `R$ ${numericValue.toFixed(2).replace('.', ',')}`;
      }
    }

    async calculate() {
      if (this.isCalculating) return;

      const cep = this.getCep();
      if (cep.length !== 8) {
        this.showMessage('Digite um CEP válido com 8 números.', true);
        this.input?.focus();
        return;
      }

      const variantId = this.getVariantId();
      if (!variantId) {
        this.showMessage('Selecione uma variação disponível antes de calcular o frete.', true);
        return;
      }

      this.clearFeedback();
      this.setLoading(true);

      let previewKey = null;

      try {
        await this.cleanupStalePreviewItems();
        previewKey = await this.addPreviewItem(variantId, this.getQuantity());
        this.previewKey = previewKey;

        const address = await this.lookupCep(cep);
        const rates = await this.fetchShippingRates(cep, address);
        this.showRates(rates);
      } catch (error) {
        console.error('[ECOMHARD] Erro ao calcular frete:', error);
        this.showMessage(error.message || this.dataset.errorLabel || 'Não foi possível calcular o frete. Tente novamente.', true);
      } finally {
        try {
          if (previewKey) await this.removePreviewItem(previewKey);
        } catch (cleanupError) {
          console.error('[ECOMHARD] Erro ao remover item temporário do frete:', cleanupError);
        }

        this.previewKey = null;
        this.setLoading(false);
      }
    }

    async addPreviewItem(variantId, quantity) {
      const propertyName = this.dataset.previewProperty || '_ecomhard_frete_preview';
      const propertyValue = `${this.dataset.previewToken || 'frete'}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity,
            properties: {
              [propertyName]: propertyValue
            }
          }]
        })
      });

      const data = await this.readJson(response);
      if (!response.ok) {
        throw new Error(data?.description || data?.message || this.dataset.errorLabel || 'Não foi possível calcular o frete.');
      }

      const addedItems = Array.isArray(data?.items) ? data.items : [data];
      const addedItem = addedItems.find((item) => item?.properties?.[propertyName] === propertyValue) || addedItems[0];
      if (addedItem?.key) return addedItem.key;

      const cart = await this.fetchCart();
      const matchedItem = cart.items.find((item) => item.properties?.[propertyName] === propertyValue);
      if (!matchedItem?.key) throw new Error(this.dataset.errorLabel || 'Não foi possível calcular o frete.');

      return matchedItem.key;
    }

    async lookupCep(cep) {
      const stateNames = {
        AC: 'Acre',
        AL: 'Alagoas',
        AP: 'Amapá',
        AM: 'Amazonas',
        BA: 'Bahia',
        CE: 'Ceará',
        DF: 'Distrito Federal',
        ES: 'Espírito Santo',
        GO: 'Goiás',
        MA: 'Maranhão',
        MT: 'Mato Grosso',
        MS: 'Mato Grosso do Sul',
        MG: 'Minas Gerais',
        PA: 'Pará',
        PB: 'Paraíba',
        PR: 'Paraná',
        PE: 'Pernambuco',
        PI: 'Piauí',
        RJ: 'Rio de Janeiro',
        RN: 'Rio Grande do Norte',
        RS: 'Rio Grande do Sul',
        RO: 'Rondônia',
        RR: 'Roraima',
        SC: 'Santa Catarina',
        SP: 'São Paulo',
        SE: 'Sergipe',
        TO: 'Tocantins'
      };

      const normalizeAddress = (data) => {
        if (!data || data.erro) return null;

        const stateCode = String(data.uf || data.state || '').trim().toUpperCase();
        const stateName = String(data.estado || stateNames[stateCode] || '').trim();
        const city = String(data.localidade || data.city || '').trim();

        if (!stateCode && !stateName) return null;

        return {
          province: stateName || stateCode,
          provinceCode: stateCode,
          city
        };
      };

      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
          headers: { Accept: 'application/json' }
        });

        if (response.ok) {
          const address = normalizeAddress(await response.json());
          if (address) return address;
        }
      } catch (error) {
        console.warn('[ECOMHARD] ViaCEP indisponível:', error);
      }

      try {
        const response = await fetch(`https://brasilapi.com.br/api/cep/v2/${cep}`, {
          headers: { Accept: 'application/json' }
        });

        if (response.ok) {
          const address = normalizeAddress(await response.json());
          if (address) return address;
        }
      } catch (error) {
        console.warn('[ECOMHARD] BrasilAPI indisponível:', error);
      }

      throw new Error('CEP não encontrado. Verifique os números digitados e tente novamente.');
    }

    async fetchShippingRates(cep, address) {
      const params = new URLSearchParams();
      params.set('shipping_address[zip]', cep);
      params.set('shipping_address[country]', this.dataset.country || 'Brazil');
      params.set('shipping_address[province]', address.province);

      if (address.city) {
        params.set('shipping_address[city]', address.city);
      }

      const query = params.toString();
      const prepareResponse = await fetch(`${this.root}cart/prepare_shipping_rates.json?${query}`, {
        method: 'POST',
        headers: { Accept: 'application/json' }
      });

      if (!prepareResponse.ok) {
        const errorData = await this.readJson(prepareResponse);
        throw new Error(this.extractShippingError(errorData));
      }

      for (let attempt = 0; attempt < 12; attempt += 1) {
        await delay(attempt === 0 ? 250 : 500);

        const response = await fetch(`${this.root}cart/async_shipping_rates.json?${query}`, {
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) {
          const errorData = await this.readJson(response);
          throw new Error(this.extractShippingError(errorData));
        }

        const data = await this.readJson(response);
        if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
      }

      const fallbackResponse = await fetch(`${this.root}cart/shipping_rates.json?${query}`, {
        headers: { Accept: 'application/json' }
      });
      const fallbackData = await this.readJson(fallbackResponse);

      if (!fallbackResponse.ok) {
        throw new Error(this.extractShippingError(fallbackData));
      }

      return fallbackData?.shipping_rates || [];
    }

    extractShippingError(data) {
      if (!data) return this.dataset.errorLabel || 'Não foi possível calcular o frete. Tente novamente.';

      const rawMessage = typeof data === 'string'
        ? data
        : data.description || data.message || Object.values(data)
          .flatMap((value) => Array.isArray(value) ? value : [value])
          .filter(Boolean)
          .map(String)
          .join(' ');

      if (/select a state|province.*required|state.*required/i.test(rawMessage || '')) {
        return 'Não foi possível identificar o estado deste CEP. Verifique o CEP e tente novamente.';
      }

      return rawMessage || this.dataset.errorLabel || 'Não foi possível calcular o frete. Tente novamente.';
    }

    async cleanupStalePreviewItems() {
      const propertyName = this.dataset.previewProperty || '_ecomhard_frete_preview';
      const cart = await this.fetchCart();
      const staleItems = cart.items.filter((item) => item.properties?.[propertyName]);

      for (const item of staleItems) {
        await this.removePreviewItem(item.key);
      }
    }

    async fetchCart() {
      const response = await fetch(`${this.root}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      const data = await this.readJson(response);

      if (!response.ok) throw new Error(this.dataset.errorLabel || 'Não foi possível acessar o carrinho.');
      return data;
    }

    async removePreviewItem(key) {
      if (!key) return;

      const response = await fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      if (!response.ok) {
        const data = await this.readJson(response);
        throw new Error(data?.description || data?.message || 'Não foi possível restaurar o carrinho.');
      }
    }

    async readJson(response) {
      const text = await response.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch (_) {
        return null;
      }
    }
  }

  customElements.define('ecomhard-frete', EcomhardFrete);
})();
